import {Component, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FileTableComponent} from '@app/files/file-table/file-table.component';
import {PermissionsTableComponent} from '@app/permissions/permissions-table/permissions-table.component';
import {mapUsersToPermissionRows, PermissionRow, PermissionsContext} from '@app/permissions/permissions.mapper';
import {pdcRoutes, pdeRoutes} from '@app/routing/routes';
import {ContactBase, ProjectResponse, ProjectsService} from '@backend';
import {PrivateProjectPageData} from '@project/project-base.model';
import {ProjectBaseAbstract} from '@project/project-base/project-base.abstract';
import {ProjectBaseComponent} from '@project/project-base/project-base.component';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {ProjectRoleOptions} from '@shared/labels/project-role';

@Component({
  selector: 'app-private-project',
  standalone: true,
  imports: [MatButton, MatIcon, RouterLink, ProjectBaseComponent, PermissionsTableComponent, FileTableComponent],
  templateUrl: './private-project.component.html',
})
export class PrivateProjectComponent extends ProjectBaseAbstract implements SectionHeaderActions {
  privateProject = signal<ProjectResponse | null>(null);
  projectsService!: ProjectsService;
  permissionsContext!: PermissionsContext;
  permissionRows = signal<PermissionRow[]>([]);

  constructor(route: ActivatedRoute) {
    super();
    route.data.subscribe(d => {
      const {project, privateProject, projectsService, datasetTiles, resourceTiles} = <PrivateProjectPageData>(
        d['projectData']
      );

      this.project.set(project);
      this.privateProject.set(privateProject);

      this.projectsService = projectsService;
      this.permissionsContext = {
        type: 'project',
        id: privateProject.id,
        editable: false,
        showEditButton: project.flags._can_manage_permission,
        orgName: privateProject.storage_organization.full_name,
      };

      this.permissionRows.set(
        mapUsersToPermissionRows(privateProject.project_users, this.permissionsContext, ProjectRoleOptions),
      );

      this.setProjectData(project, datasetTiles, resourceTiles);
      // Use the privateProject data
      this.project.set({
        ...project,
        ...privateProject,
      });
    });
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/view-private-project',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  protected buildMetadataTable() {
    const contact = this.privateProject().contacts as ContactBase | undefined;
    const hasContact = !!(contact?.name || contact?.email || contact?.phone_number);

    this.metaTableData = [
      {
        key: 'keywords',
        display_name: 'Keywords',
        value: this.privateProject().keywords.join(', '),
      },
      {
        key: 'pi',
        display_name: 'Project Lead / Principal Investigator Email(s)',
        value: this.privateProject()
          .project_users.filter(u => u.is_project_pi)
          .map(u => u.user.email)
          .join(', '),
      },
      ...(hasContact
        ? [
            {
              key: 'contact',
              display_name: 'Contact',
              value: [contact.name ?? '', contact.email ?? '', contact.phone_number ?? ''],
            },
          ]
        : []),
      {
        key: 'storage_organization',
        display_name: 'Storage Organization',
        value: this.privateProject().storage_organization.full_name,
      },
      {
        key: 'partners',
        display_name: 'Partner Institution(s)',
        value: this.privateProject().partners.join(', '),
      },
      {
        key: 'funders',
        display_name: 'Funding Source(s)',
        value: this.privateProject().funders.join(', '),
      },
      {
        key: 'website',
        display_name: 'Website',
        value: (this.privateProject().web_page_urls ?? []).join(', '),
        link: this.privateProject().web_page_urls?.length === 1 ? this.privateProject().web_page_urls[0] : undefined,
      },
    ];
  }

  protected readonly pdcRoutes = pdcRoutes;
  protected readonly pdeRoutes = pdeRoutes;

  getEditLink() {
    if (!this.permissionsContext) return undefined;
    return pdeRoutes.projectEditAccess(this.permissionsContext.id);
  }
}
